import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Mail, Settings, Send, Check, X, AlertCircle, Loader2, 
  RefreshCw, Eye, Clock, User, FileText, ChevronRight,
  Server, Lock, AtSign, MailCheck, MailX, TestTube
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEmails = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // SMTP Config
  const [config, setConfig] = useState({
    host: '',
    port: 465,
    user: '',
    password: '',
    from_email: 'contact@jardily.fr',
    to_email: 'contact@jardily.fr',
    use_ssl: true
  });
  
  // Email logs
  const [emailLogs, setEmailLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    // Wait a bit for token to be available in localStorage
    const timer = setTimeout(() => {
      const token = getToken();
      if (token) {
        fetchConfig();
        fetchEmailLogs();
        fetchStats();
      } else {
        setLoading(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token && filterType !== undefined) {
      fetchEmailLogs();
    }
  }, [filterType, filterStatus]);

  const getToken = () => localStorage.getItem('jardily_token');

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/smtp-config`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConfig(res.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      let url = `${API}/email-logs?limit=100`;
      if (filterType) url += `&email_type=${filterType}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setEmailLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/email-logs/stats/summary`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchEmailDetail = async (logId) => {
    try {
      const res = await axios.get(`${API}/email-logs/${logId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedEmail(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/smtp-config`, config, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Configuration SMTP sauvegardée !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const testSmtp = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/smtp-test`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(res.data.message);
      fetchEmailLogs();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec du test');
    } finally {
      setTesting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"><MailCheck className="w-3 h-3" /> Envoyé</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"><MailX className="w-3 h-3" /> Échec</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium"><Clock className="w-3 h-3" /> En attente</span>;
      default:
        return <span className="px-2 py-1 bg-stone-100 text-stone-700 rounded-full text-xs">{status}</span>;
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      'quote_notification': { label: 'Devis (admin)', color: 'bg-purple-100 text-purple-700' },
      'quote_confirmation': { label: 'Devis (client)', color: 'bg-purple-50 text-purple-600' },
      'contact_notification': { label: 'Contact (admin)', color: 'bg-blue-100 text-blue-700' },
      'contact_confirmation': { label: 'Contact (client)', color: 'bg-blue-50 text-blue-600' },
      'test': { label: 'Test', color: 'bg-stone-100 text-stone-600' },
    };
    const t = types[type] || { label: type, color: 'bg-stone-100 text-stone-600' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${t.color}`}>{t.label}</span>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl text-stone-900">Gestion des emails</h1>
          <p className="text-stone-600 text-sm mt-1">
            Configurez votre serveur SMTP et consultez l'historique des emails envoyés
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
                  <p className="text-xs text-stone-500">Total envoyés</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <MailCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.sent}</p>
                  <p className="text-xs text-stone-500">Réussis</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <MailX className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                  <p className="text-xs text-stone-500">Échoués</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  <p className="text-xs text-stone-500">En attente</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-stone-100 p-1 rounded-xl">
            <TabsTrigger value="config" className="rounded-lg data-[state=active]:bg-white">
              <Settings className="w-4 h-4 mr-2" />
              Configuration SMTP
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-white">
              <Mail className="w-4 h-4 mr-2" />
              Historique des emails
            </TabsTrigger>
          </TabsList>

          {/* SMTP Configuration Tab */}
          <TabsContent value="config" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="font-semibold text-lg text-stone-900 mb-6 flex items-center gap-2">
                <Server className="w-5 h-5 text-emerald-600" />
                Configuration du serveur SMTP
              </h3>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Serveur SMTP (Host)</Label>
                  <div className="relative">
                    <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      value={config.host}
                      onChange={(e) => setConfig({ ...config, host: e.target.value })}
                      placeholder="mail.exemple.fr"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-stone-500">Ex: mail.planethoster.net</p>
                </div>

                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    value={config.port}
                    onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) || 465 })}
                    placeholder="465"
                  />
                  <p className="text-xs text-stone-500">465 pour SSL, 587 pour TLS</p>
                </div>

                <div className="space-y-2">
                  <Label>Utilisateur SMTP</Label>
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      value={config.user}
                      onChange={(e) => setConfig({ ...config, user: e.target.value })}
                      placeholder="contact@jardily.fr"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Mot de passe SMTP</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      type="password"
                      value={config.password}
                      onChange={(e) => setConfig({ ...config, password: e.target.value })}
                      placeholder="••••••••"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email expéditeur</Label>
                  <Input
                    value={config.from_email}
                    onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                    placeholder="contact@jardily.fr"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email de réception (notifications)</Label>
                  <Input
                    value={config.to_email}
                    onChange={(e) => setConfig({ ...config, to_email: e.target.value })}
                    placeholder="contact@jardily.fr"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.use_ssl}
                      onChange={(e) => setConfig({ ...config, use_ssl: e.target.checked })}
                      className="w-5 h-5 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="font-medium text-stone-900">Utiliser SSL</span>
                      <p className="text-xs text-stone-500">Cochez si vous utilisez le port 465</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-4 mt-8 pt-6 border-t border-stone-200">
                <Button onClick={saveConfig} disabled={saving} className="btn-primary">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Sauvegarder
                    </>
                  )}
                </Button>
                <Button onClick={testSmtp} disabled={testing} variant="outline">
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Envoyer un email de test
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Help box */}
            <div className="bg-blue-50 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Configuration Planethoster</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Serveur SMTP :</strong> mail.votre-domaine.fr ou smtp.planethoster.net</li>
                    <li>• <strong>Port :</strong> 465 (SSL) ou 587 (TLS)</li>
                    <li>• <strong>Utilisateur :</strong> Votre adresse email complète (ex: contact@jardily.fr)</li>
                    <li>• <strong>Mot de passe :</strong> Le mot de passe de votre boîte email</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Email History Tab */}
          <TabsContent value="history" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg text-stone-900">Historique des emails</h3>
                <Button onClick={fetchEmailLogs} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b border-stone-200">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-stone-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Tous les types</option>
                  <option value="quote_notification">Devis (admin)</option>
                  <option value="quote_confirmation">Devis (client)</option>
                  <option value="contact_notification">Contact (admin)</option>
                  <option value="contact_confirmation">Contact (client)</option>
                  <option value="test">Test</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-stone-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">Tous les statuts</option>
                  <option value="sent">Envoyés</option>
                  <option value="failed">Échoués</option>
                  <option value="pending">En attente</option>
                </select>
              </div>

              {/* Email list */}
              {emailLogs.length === 0 ? (
                <div className="text-center py-12 text-stone-500">
                  <Mail className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Aucun email dans l'historique</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {emailLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors cursor-pointer"
                      onClick={() => fetchEmailDetail(log.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getTypeBadge(log.email_type)}
                          {getStatusBadge(log.status)}
                        </div>
                        <p className="font-medium text-stone-900 truncate">{log.subject}</p>
                        <p className="text-sm text-stone-500 truncate">
                          <User className="w-3 h-3 inline mr-1" />
                          {log.to_email}
                          {log.recipient_name && ` (${log.recipient_name})`}
                        </p>
                      </div>
                      <div className="text-right text-sm text-stone-500">
                        <p>{formatDate(log.created_at)}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Email Detail Dialog */}
        <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Détail de l'email
              </DialogTitle>
            </DialogHeader>
            
            {selectedEmail && (
              <div className="space-y-6">
                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-stone-50 rounded-xl">
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Destinataire</p>
                    <p className="font-medium">{selectedEmail.to_email}</p>
                    {selectedEmail.recipient_name && (
                      <p className="text-sm text-stone-600">{selectedEmail.recipient_name}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Date d'envoi</p>
                    <p className="font-medium">{formatDate(selectedEmail.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Type</p>
                    {getTypeBadge(selectedEmail.email_type)}
                  </div>
                  <div>
                    <p className="text-xs text-stone-500 mb-1">Statut</p>
                    {getStatusBadge(selectedEmail.status)}
                  </div>
                  {selectedEmail.error_message && (
                    <div className="col-span-2">
                      <p className="text-xs text-stone-500 mb-1">Erreur</p>
                      <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{selectedEmail.error_message}</p>
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <p className="text-xs text-stone-500 mb-1">Sujet</p>
                  <p className="font-semibold text-lg">{selectedEmail.subject}</p>
                </div>

                {/* Preview */}
                <div>
                  <p className="text-xs text-stone-500 mb-2">Aperçu de l'email</p>
                  <div 
                    className="border border-stone-200 rounded-xl overflow-hidden"
                    style={{ maxHeight: '500px', overflow: 'auto' }}
                  >
                    <iframe
                      srcDoc={selectedEmail.full_html}
                      title="Email preview"
                      className="w-full h-[500px] border-0"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminEmails;
